import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "user-interface-ui"
    }}>{`User Interface (UI)`}</h1>
    <p>{`In UXP, you can write the UI in three ways - using standard HTML Elements, built-in Spectrum UXP widgets, and Spectrum Web Components (recently added).`}</p>
    <InlineAlert variant="info" slots="header, text1, text2" mdxType="InlineAlert" />
    <p>{`Scripts and plugins`}</p>
    <p><strong parentName="p">{`In scripts`}</strong>{`, you can only create modal dialogs.`}</p>
    <p><strong parentName="p">{`In plugins`}</strong>{`, you can create persistent plugin panels, visible at all times. Or command plugins, that can create modal dialogs only.`}</p>
    <p>{`But before we proceed, it's important to understand some terminology`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Spectrum`}</strong>{` - An open-source design language and guidelines provided by Adobe.`}</li>
      <li parentName="ul"><strong parentName="li">{`Web components`}</strong>{` - HTML5 Web technology lets you define custom HTML tags.`}</li>
      <li parentName="ul"><strong parentName="li">{`Adobe Spectrum Web Components`}</strong>{` (popularly known as SWC) - An open-source library that has Spectrum-styled web components.`}</li>
    </ul>
    <p>{`With that in mind, let's get into the details:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`HTML elements`}</strong>{` `}<br></br>{`
These are pure HTML elements following web standards. For example, `}<inlineCode parentName="p">{`<img>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<dialog>`}</inlineCode>{` etc. You have the flexibility to style them with your custom stylesheet. `}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Spectrum UXP widgets`}</strong>{` `}<br></br>{`
These are built-in Spectrum-styled UXP components. These widgets mimic the Adobe SWC library APIs and expose a subset of the functionality of the corresponding SWC. `}</p>
        <p parentName="li">{` You can use them as you would any other HTML tag. For example,`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<sp-button variant="primary">I'm a Spectrum button</sp-button>
`}</code></pre>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`UXP Spectrum web components (Beta)`}</strong>{` `}<br></br>{`
These are the Adobe Spectrum Web Components, recently added and supported by UXP v7.0 onwards. It's built using Web Components technology.`}</p>
        <p parentName="li">{`  To use them, you require to install the components individually and then `}<inlineCode parentName="p">{`import`}</inlineCode>{` them before using the component tag. For example,`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`npm i @spectrum-web-components/button@0.19.8
`}</code></pre>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-js"
          }}>{`import '@spectrum-web-components/button/sp-button.js';
`}</code></pre>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<sp-button variant="primary">I'm a SWC button</sp-button>
`}</code></pre>
        <p parentName="li">{`  Note that although the tag appears to be identical to Spectrum UXP widgets, the way they are implemented is fundamentally different. Read on to learn more about the differences.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "differences"
    }}>{`Differences`}</h3>
    <p>{`Now that you know the various options, you must understand their differences and limitations`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`HTML elements`}</strong>{` `}<br></br>{`
Keep in mind that UXP is not a browser. It's a platform that provides the ease of using web technologies to build plugins/scripts for desktop applications. Hence, it does not support all the HTML/CSS capabilities you can use in a browser. In fact, the tags that are `}<em parentName="p">{`not`}</em>{` supported are treated like simple `}<inlineCode parentName="p">{`<div>`}</inlineCode>{`.`}</p>
        <p parentName="li">{` Do take a look at the list of HTML `}<a parentName="p" {...{
            "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-html/General/Unsupported%20Elements/"
          }}>{`elements`}</a>{`, and `}<a parentName="p" {...{
            "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-html/General/Unsupported%20Attributes/"
          }}>{`attributes`}</a>{` that are `}<em parentName="p">{`not`}</em>{` supported.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Spectrum UXP widgets`}</strong><br></br>{`
Remember that these are built-in within UXP. Therefore, they may appear to be a black-box solution that does not allow you to peek into the details while debugging or altering their behavior easily.`}</p>
      </li>
    </ul>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`UXP Spectrum web components (Beta)`}</strong>{` `}<br></br>{`
Requires basic knowledge of `}<a parentName="p" {...{
            "href": "https://javascript.info/web-components"
          }}>{`Web Components`}</a>{`. And since this is in the beta phase and we are working on supporting more components in the future, you may not find all the components you need. `}</p>
        <p parentName="li">{` Moreover, these components follow the principles of SWC and web components, which means`}</p>
        <ol parentName="li">
          <li parentName="ol">{`You need to `}<inlineCode parentName="li">{`import`}</inlineCode>{` individual comps before using them`}</li>
          <li parentName="ol">{`You can debug SWC like a regular Web Component. `}</li>
        </ol>
      </li>
    </ul>
    <p>{`That said, you can mix and match these three offerings to suit your use case. For example, the following will work seamlessly`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-HTML"
      }}>{`<form> <!-- plain HTML element -->
   <sp-banner> <!-- Spectrum Web Component -->
     <div slot="header">Header text</div>
     <div slot="content">Content of the banner</div>
  </sp-banner>
  <sp-button variant="primary">I'm a button</sp-button> <!-- Spectrum widget -->
</form>
`}</code></pre>
    <h3 {...{
      "id": "whats-best-for-you"
    }}>{`What's best for you?`}</h3>
    <p>{`The choice you make depends on your use case and the stage of development you are in.`}</p>
    <p>{`If you are looking for a ready-made Adobe Spectrum-styled UI or are just getting started with programming your UI, we recommend you start your journey with SWC. Fall back to Spectrum UXP widgets for the comps that are not available yet. And use plain HTML elements when they are not available in either.`}</p>
    <p>{`However, if you prefer vanilla HTML and don't mind undertaking the efforts of styling, use the HTML elements along with your custom stylesheet. Make sure you read the `}<a parentName="p" {...{
        "href": "../../recipes/css-styling/"
      }}>{`CSS Styling`}</a>{` recipe.`}</p>
    {/* 
     // TODO add once the starter templates are ready
     ## Additional notes
     While writing a simple JS/HTML/CSS plugin is possible, those who are building plugins that have internal state management, or more complicated UI may benefit from commonly used UI libraries. The below examples are not necessarily only ones you can use but can serve as good starters if you already are familiar with a library.
     We do not suggest that you use any particular library for your plugins but suggest that you read about them and decide for yourself what will work best for you.
     ### React
     [React.js](https://reactjs.org/) is one of the most used UI libraries. It's declarative and component-based, helping you break your UI into reusable components. 
     See [react-starter](https://github.com/AdobeDocs/uxp-indesign-samples/plugins/react-starter) for the sample project.
     We soon will be adding more samples for other popular frameworks such as Vue, and Svelte.
     
     ### Vue
     [Vue.js](https://vuejs.org/) is another declarative UI library that's easy to learn. 
     See [ui-vue-starter](https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/ui-vue-starter) for the sample project.  // TODO update link
     ### Svelte
     [Svelte](https://svelte.dev/) is different from the other two libraries, in the sense that it compiles your code to a simple JS application, shrinking its size.
     See [ui-svelte-starter](https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/ui-svelte-starter) for the sample project.  // TODO update link
     */}
    <h2 {...{
      "id": "reference-material"
    }}>{`Reference material`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-spectrum/swc/"
        }}>{`Spectrum Web Components`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-spectrum/Spectrum%20UXP%20Widgets/"
        }}>{`Spectrum UXP widgets`}</a></li>
      <li parentName="ul">{`Supported HTML `}<a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-html/"
        }}>{`tags`}</a>{` and `}<a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Global%20Members/HTML%20Elements/"
        }}>{`elements`}</a>
        {/* [Kitchen sink sample]() - //TODO link to Id starter/ kitchen sink sample */}
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      